"use client";

import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import cn from "clsx";

import {
  DEFAULT_SIZES,
  getResponsiveImageProps,
  ImageSize,
  ImageSource,
  imageUtils,
} from "@/types/image.types";
import {
  getSmartFallbackUrl,
  isOptimizedGcsUrl,
} from "@/utils/image-fallback.utils";

export interface OptimizedImageProps {
  // Image source - can be any type
  src?: ImageSource;
  blobSrc?: ImageSource; // For smooth transitions (e.g. blob -> server)
  serverSrc?: ImageSource; // Server image (for transitions)

  // Next.js Image props
  alt: string;
  className?: string;
  fill?: boolean;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
  loading?: "eager" | "lazy";
  unoptimized?: boolean;

  // Optimization options
  imageSize?: ImageSize;
  enableTransition?: boolean; // Enable smooth transition between blob/server
  transitionDuration?: number; // Transition duration in ms

  // Upload status for transition logic
  uploadStatus?: "pending" | "uploading" | "success" | "error";

  // Callbacks
  onLoad?: () => void;
  onError?: () => void;

  // Style overrides
  style?: React.CSSProperties;
}

// Cache for processed image props to prevent re-computation
const imagePropsCache = new Map<string, any>();

export const OptimizedImage: React.FC<OptimizedImageProps> = React.memo(
  ({
    src,
    blobSrc,
    serverSrc,
    alt,
    className,
    fill = false,
    width,
    height,
    sizes = DEFAULT_SIZES,
    priority = false,
    loading = "lazy",
    unoptimized = false,
    imageSize = "medium",
    enableTransition = false,
    transitionDuration = 1000,
    uploadStatus,
    onLoad,
    onError,
    style,
  }) => {
    const [serverImageLoaded, setServerImageLoaded] = useState(false);
    const [blobImageLoaded, setBlobImageLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Reset states when sources change
    useEffect(() => {
      if (serverSrc && uploadStatus === "success") {
        setServerImageLoaded(false);
      }
    }, [serverSrc, uploadStatus]);

    useEffect(() => {
      if (blobSrc) {
        setBlobImageLoaded(false);
      }
    }, [blobSrc]);

    useEffect(() => {
      if (src) {
        setHasError(false);
      }
    }, [src]);

    // Determine which image to show
    const primarySrc = src || blobSrc || serverSrc;

    // For transitions, determine if we should show server image
    const showServerImage =
      enableTransition &&
      serverSrc &&
      uploadStatus === "success" &&
      serverImageLoaded;

    const showBlobImage =
      enableTransition &&
      blobSrc &&
      (!serverSrc || uploadStatus !== "success" || !serverImageLoaded);

    // Smart fallback logic for optimized images
    const getSmartFallbackSrc = useCallback((imageSource: ImageSource) => {
      if (
        !imageSource ||
        typeof imageSource !== "object" ||
        !("original" in imageSource)
      ) {
        return imageSource;
      }

      const optimizedImage = imageSource as any;

      // Try different sizes in order of preference based on device
      const fallbackOrder = [
        optimizedImage.desktop,
        optimizedImage.tablet,
        optimizedImage.mobile,
        optimizedImage.original,
      ].filter(Boolean);

      return fallbackOrder[0] || optimizedImage.original;
    }, []);

    // Create stable cache key for memoization
    const cacheKey = useMemo(() => {
      const sources = [primarySrc, blobSrc, serverSrc]
        .map((s) => {
          if (typeof s === "string") {
            return s;
          }
          if (imageUtils.isFileObject(s)) {
            return `file_${s.name}_${s.size}`;
          }
          if (imageUtils.isOptimizedImage(s)) {
            return s.id;
          }
          return "none";
        })
        .join("|");

      return `${sources}|${imageSize}|${sizes}|${fill}|${width}|${height}`;
    }, [primarySrc, blobSrc, serverSrc, imageSize, sizes, fill, width, height]);

    // Get optimized image props with caching
    const imageProps = useMemo(() => {
      if (imagePropsCache.has(cacheKey)) {
        return imagePropsCache.get(cacheKey);
      }

      const primaryProps = primarySrc
        ? getResponsiveImageProps(getSmartFallbackSrc(primarySrc), sizes)
        : { src: "" };
      const blobProps = blobSrc
        ? getResponsiveImageProps(getSmartFallbackSrc(blobSrc), sizes)
        : { src: "" };
      const serverProps = serverSrc
        ? getResponsiveImageProps(getSmartFallbackSrc(serverSrc), sizes)
        : { src: "" };

      const result = { primaryProps, blobProps, serverProps };
      imagePropsCache.set(cacheKey, result);

      // Clean cache if it gets too large
      if (imagePropsCache.size > 100) {
        const keysToDelete = Array.from(imagePropsCache.keys()).slice(0, 20);
        keysToDelete.forEach((key) => imagePropsCache.delete(key));
      }

      return result;
    }, [cacheKey, primarySrc, blobSrc, serverSrc, sizes, getSmartFallbackSrc]);

    // Handle image load events
    const handleBlobImageLoad = useCallback(() => {
      setBlobImageLoaded(true);
      if (!enableTransition) {
        onLoad?.();
      }
    }, [enableTransition, onLoad]);

    const handleServerImageLoad = useCallback(() => {
      setServerImageLoaded(true);
      onLoad?.();
    }, [onLoad]);

    const handlePrimaryImageLoad = useCallback(() => {
      onLoad?.();
    }, [onLoad]);

    const handleImageError = useCallback(() => {
      setHasError(true);
      onError?.();
    }, [onError]);

    // Enhanced error handling with automatic fallback
    const handleImageErrorWithFallback = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        const currentSrc = img.src;

        // Try smart fallback for optimized GCS URLs
        if (isOptimizedGcsUrl(currentSrc)) {
          const fallbackUrl = getSmartFallbackUrl(currentSrc);
          if (fallbackUrl && fallbackUrl !== currentSrc) {
            img.src = fallbackUrl;
            return; // Don't call error handler yet, try fallback first
          }
        }

        // If this is an optimized image object, try original
        if (
          typeof primarySrc === "object" &&
          primarySrc &&
          "original" in primarySrc
        ) {
          const optimizedImage = primarySrc as any;

          // Try original as last resort
          if (
            currentSrc !== optimizedImage.original &&
            optimizedImage.original
          ) {
            img.src = optimizedImage.original;
            return; // Don't call error handler yet, try original first
          }
        }

        // If all fallbacks failed, call original error handler
        handleImageError();
      },
      [primarySrc, handleImageError],
    );

    // Common image style
    const imageStyle = useMemo(
      () => ({
        ...style,
      }),
      [style],
    );

    // Memoized styles for blob image to prevent re-creation on each render
    const blobImageStyle = useMemo(
      () => ({
        ...imageStyle,
        position: (fill
          ? "absolute"
          : "relative") as React.CSSProperties["position"],
        zIndex: 1,
      }),
      [imageStyle, fill],
    );

    // Memoized styles for server image to prevent re-creation on each render
    const serverImageStyle = useMemo(
      () => ({
        ...imageStyle,
        position: "absolute" as React.CSSProperties["position"],
        top: 0,
        left: 0,
        zIndex: 2,
      }),
      [imageStyle],
    );

    // Transition styles
    const transitionClass = enableTransition
      ? "transition-opacity duration-300"
      : "";

    // Memoized className for blob image
    const blobImageClassName = useMemo(
      () =>
        cn(className, transitionClass, {
          "opacity-0": showServerImage,
          "opacity-100": showBlobImage,
        }),
      [className, transitionClass, showServerImage, showBlobImage],
    );

    // Memoized className for server image
    const serverImageClassName = useMemo(
      () =>
        cn(className, transitionClass, {
          "opacity-100": serverImageLoaded,
          "opacity-0": !serverImageLoaded,
        }),
      [className, transitionClass, serverImageLoaded],
    );

    // If transition is disabled, render single image
    if (!enableTransition || (!blobSrc && !serverSrc)) {
      const props = imageProps.primaryProps;
      if (!props.src || hasError) {
        return (
          <div
            className={cn(
              "bg-gray-100 flex items-center justify-center",
              className,
            )}
          >
            <span className="text-gray-400 text-sm">No image</span>
          </div>
        );
      }

      return (
        <Image
          src={props.src}
          alt={alt}
          className={cn(className, transitionClass)}
          fill={fill}
          width={!fill ? width : undefined}
          height={!fill ? height : undefined}
          sizes={props.sizes}
          priority={priority}
          loading={priority ? "eager" : loading}
          unoptimized={unoptimized || imageUtils.isFileObject(primarySrc)}
          style={imageStyle}
          onLoad={handlePrimaryImageLoad}
          onError={handleImageErrorWithFallback}
        />
      );
    }

    // Render with transition between blob and server images
    return (
      <div className="relative w-full h-full">
        {/* Blob image - visible initially, hidden when server image loads */}
        {blobSrc && imageProps.blobProps.src && (
          <Image
            src={imageProps.blobProps.src}
            alt={alt}
            className={blobImageClassName}
            fill={fill}
            width={!fill ? width : undefined}
            height={!fill ? height : undefined}
            sizes={imageProps.blobProps.sizes}
            priority={priority}
            loading={priority ? "eager" : loading}
            unoptimized={imageUtils.isFileObject(blobSrc)}
            style={blobImageStyle}
            onLoad={handleBlobImageLoad}
            onError={handleImageErrorWithFallback}
          />
        )}

        {/* Server image - hidden initially, visible when loaded */}
        {serverSrc &&
          uploadStatus === "success" &&
          imageProps.serverProps.src && (
            <Image
              src={imageProps.serverProps.src}
              alt={alt}
              className={serverImageClassName}
              fill={fill}
              width={!fill ? width : undefined}
              height={!fill ? height : undefined}
              sizes={imageProps.serverProps.sizes}
              priority={false} // Server image is not priority
              loading="lazy"
              unoptimized={imageUtils.isFileObject(serverSrc)}
              style={serverImageStyle}
              onLoad={handleServerImageLoad}
              onError={handleImageError}
            />
          )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    return (
      prevProps.src === nextProps.src &&
      prevProps.blobSrc === nextProps.blobSrc &&
      prevProps.serverSrc === nextProps.serverSrc &&
      prevProps.uploadStatus === nextProps.uploadStatus &&
      prevProps.alt === nextProps.alt &&
      prevProps.className === nextProps.className &&
      prevProps.fill === nextProps.fill &&
      prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height &&
      prevProps.sizes === nextProps.sizes &&
      prevProps.imageSize === nextProps.imageSize &&
      prevProps.enableTransition === nextProps.enableTransition &&
      prevProps.priority === nextProps.priority
      // Note: we intentionally exclude onLoad/onError from comparison
    );
  },
);

OptimizedImage.displayName = "OptimizedImage";

export default OptimizedImage;
